import { useCallback, useEffect, useMemo, useState } from 'react';

// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import { v4 as uuid } from 'uuid';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { EditorActions } from '@atlaskit/editor-core';
import { token } from '@atlaskit/tokens';
import { DEFAULT_GRAY_COLOR } from '@atlassian/color-picker';
import {
	ITEM_FIELDS as CARD_FIELDS,
	checkItems,
	CornerStyle,
	RenderVariant,
	SegmentationTargets,
	updateNodeParameters,
	type UseExtensionParametersAndActionsProps,
	useExtensionsActions,
} from '@atlassian/company-hub-common';

import { CARDS_EXTENSION_VERSION_5, CARDS_IMAGES_V5 } from '../../constants';
import {
	Alignment,
	type Card,
	type CardsExtensionParameters,
	CardsLayout,
	ImagePosition,
	Orientation,
} from '../../types';
import type { CardsActions, UpdateField } from './types';

export const cardNewParametersV4: {
	backgroundColor: 'var(--ds-surface-overlay)';
	textColor: 'var(--ds-text)';
} = {
	backgroundColor: token('elevation.surface.overlay'),
	textColor: token('color.text'),
};

const getNewCardImage = (cardsLength: number) => {
	const imageIndex = cardsLength % CARDS_IMAGES_V5.length;
	const { src, alt } = CARDS_IMAGES_V5[imageIndex] || CARDS_IMAGES_V5[0];
	return { imageSrc: src, imageAltText: alt };
};

export const createNewCardWithDefaultParameters = (defaultField?: UpdateField<Card>): Card => {
	return {
		// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
		cardId: uuid(),
		link: '',
		isLinkUserEdited: false,
		title: '',
		isTitleUserEdited: false,
		description: '',
		isDescriptionUserEdited: false,
		imagePosition: ImagePosition.MIDDLE,
		...defaultField,
	};
};

export const updateLinkAndConfluencePageId = (
	link: string,
	confluencePageId?: string,
	isLinkUserEdited?: boolean,
	linkResourceType?: string,
): {
	confluencePageId: string;
	isLinkUserEdited: boolean;
	link: string;
	linkResourceType: string;
} => {
	return {
		[CARD_FIELDS.LINK]: link,
		[CARD_FIELDS.CONFLUENCE_PAGE_ID]: confluencePageId || '',
		[CARD_FIELDS.IS_LINK_USER_EDITED]: isLinkUserEdited || false,
		[CARD_FIELDS.LINK_RESOURCE_TYPE]: linkResourceType || '',
	};
};

const fetchLocalIdMacroParameters = (
	editorActions: EditorActions,
	localId: string,
): CardsExtensionParameters => {
	const localIdNode = editorActions?.getNodeByLocalId?.(localId)?.toJSON();
	let params = localIdNode?.attrs?.parameters;

	if (params === undefined) {
		return undefined as any;
	}

	// Make a copy of the params object to avoid mutating the original object
	params = { ...params };

	// If the extension was created prior to removal of type field, remove it from object here
	// This update is/was not done in combination with a feature gate
	delete params['type'];

	params = migrateParamsToLatestVersion(params);

	return params;
};

/**
 * Migrates card extension parameters to the latest version by applying
 * default values for any missing properties introduced in each version.
 */
const migrateParamsToLatestVersion = (
	params: CardsExtensionParameters,
): CardsExtensionParameters => {
	// Version 2 defaults
	params.renderVariant ??= RenderVariant.MANUAL;

	// Version 3 defaults
	params.isAutoscrollEnabled ??= false;
	params.orientation ??= Orientation.STACKED;
	params.alignment ??= Alignment.CENTER;
	if (
		params.segmentationTarget === SegmentationTargets.ALL &&
		params.audienceSegmentationGroupIds === undefined
	) {
		params.audienceSegmentationGroupIds = params.cards[0]?.audienceSegmentationGroupIds;
	}

	// Version 4 defaults
	params.backgroundColor ??= token('elevation.surface.overlay');
	params.textColor ??= token('color.text');
	params.cornerStyle ??= CornerStyle.ROUNDED;
	params.isSocialStatsShown ??= false;
	params.isIconShown ??= false;

	// Version 5 defaults
	params.layout ??= CardsLayout.FLEXIBLE_IMAGE_HEIGHT;
	params.isPaddingEnabled ??= true;
	params.isLabelShown ??= true;
	params.borderColor ??= DEFAULT_GRAY_COLOR;

	// Set to latest version
	params.version = CARDS_EXTENSION_VERSION_5;

	return params;
};

const removeProperties = (
	extensionsPropertiesToRemove: string[],
	extensionParameters: CardsExtensionParameters,
): CardsExtensionParameters => {
	const mutatedParameters = { ...extensionParameters };

	extensionsPropertiesToRemove.forEach((field) => {
		delete mutatedParameters[field as keyof CardsExtensionParameters];
	});

	return mutatedParameters;
};

const updateCardInList = (cards: Card[], cardId: string, updatedField: UpdateField<Card>): Card[] =>
	cards.map((card) => {
		if (card.cardId === cardId) {
			return { ...card, ...updatedField };
		}
		return card;
	});

export type UseCardsParametersAndActionsReturnType = {
	cardsActions: CardsActions;
	cardsParameters: CardsExtensionParameters;
};

export const useCardsExtensionParametersAndActions = ({
	localId,
	editorActions,
}: UseExtensionParametersAndActionsProps): UseCardsParametersAndActionsReturnType => {
	const [cardsParameters, setExtensionParameters] = useState<CardsExtensionParameters>(
		fetchLocalIdMacroParameters(editorActions, localId),
	);
	const { setIsInAutoFocusMode, setSelectedToEditCardId } = useExtensionsActions();
	const { cards, segmentationTarget, isSegmentationShown } = cardsParameters ?? {};
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		const extensionNodeParams = fetchLocalIdMacroParameters(editorActions, localId);
		setExtensionParameters(extensionNodeParams);
	}, [editorActions, localId]);

	const updateExtensionParams = useCallback(
		(
			newExtensionParameters: UpdateField<CardsExtensionParameters>,
			options: { fieldsToRemove?: string[] } = {},
		) => {
			setExtensionParameters((prevParams) => {
				let mutatedFields = { ...prevParams };
				const fieldsToRemove = [...(options?.fieldsToRemove || [])];

				if (fieldsToRemove.length > 0) {
					mutatedFields = removeProperties(fieldsToRemove, prevParams);
				}

				const updatedParams = {
					...mutatedFields,
					...newExtensionParameters,
				} as CardsExtensionParameters;

				// Save params to ADF and state
				const success = updateNodeParameters(editorActions, updatedParams);

				if (!success) {
					createAnalyticsEvent({
						type: 'sendOperationalEvent',
						data: {
							source: 'cardsExtension',
							action: 'error',
							actionSubject: 'cardsConfigPanel',
							attributes: {
								updatedFields: newExtensionParameters,
								version: updatedParams.version,
							},
						},
					}).fire();
				}

				return updatedParams;
			});
		},
		[editorActions, createAnalyticsEvent],
	);

	const addCard = useCallback(() => {
		const newCard = createNewCardWithDefaultParameters({
			[CARD_FIELDS.AUDIENCE_SEGMENTATION_GROUP_IDS]:
				isSegmentationShown && segmentationTarget === SegmentationTargets.ALL
					? cards[0]?.audienceSegmentationGroupIds
					: undefined,
			...getNewCardImage(cards.length),
		});

		updateExtensionParams({
			cards: [...(checkItems(cards) || []), newCard],
		});
		setIsInAutoFocusMode(true);
		setSelectedToEditCardId(newCard.cardId);
	}, [
		cards,
		segmentationTarget,
		isSegmentationShown,
		updateExtensionParams,
		setSelectedToEditCardId,
		setIsInAutoFocusMode,
	]);

	const removeCard = useCallback(
		(cardId: string) =>
			updateExtensionParams({
				cards: cards.filter((card) => card.cardId !== cardId),
			}),
		[cards, updateExtensionParams],
	);

	const updateMainFormField = useCallback(
		(updatedFields: UpdateField<CardsExtensionParameters>, options = {}) =>
			updateExtensionParams(updatedFields, options),
		[updateExtensionParams],
	);

	const updateCardFormField = useCallback(
		(cardId: string, updatedField: UpdateField<Card>) => {
			const updatedCards = updateCardInList(cards, cardId, updatedField);

			updateExtensionParams({ cards: updatedCards });
		},
		[cards, updateExtensionParams],
	);

	const updateCardFormFieldForAllCards = useCallback(
		(updatedField: UpdateField<Card>) => {
			const updatedCards = cards.map((card) => {
				return { ...card, ...updatedField };
			});

			updateExtensionParams({ cards: updatedCards });
		},
		[cards, updateExtensionParams],
	);

	const updateLayout = useCallback(
		(newLayout: CardsLayout) => {
			updateExtensionParams({ layout: newLayout });
		},
		[updateExtensionParams],
	);

	const updateBorderColor = useCallback(
		(newBorderColor: string) => {
			updateExtensionParams({ borderColor: newBorderColor });
		},
		[updateExtensionParams],
	);

	const updateCornerStyle = useCallback(
		(newCornerStyle: CornerStyle) => {
			updateExtensionParams({ cornerStyle: newCornerStyle });
		},
		[updateExtensionParams],
	);

	const updateCornerRadius = useCallback(
		(newCornerRadius: number) => {
			updateExtensionParams({ cornerRadius: newCornerRadius });
		},
		[updateExtensionParams],
	);

	const cardsActions: CardsActions = useMemo(
		() => ({
			addCard,
			removeCard,
			updateMainFormField,
			updateCardFormField,
			updateCardFormFieldForAllCards,
			updateLayout,
			updateBorderColor,
			updateCornerRadius,
			updateCornerStyle,
		}),
		[
			addCard,
			removeCard,
			updateMainFormField,
			updateCardFormField,
			updateCardFormFieldForAllCards,
			updateLayout,
			updateBorderColor,
			updateCornerRadius,
			updateCornerStyle,
		],
	);

	return { cardsParameters, cardsActions };
};
